import React, { useState, useEffect, useCallback, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useErrorHandler } from "../utils/errorHandler";
import { SEOHead, Breadcrumb } from "../components/SEOHelpers";
import api, { testMongoDBConnectivity } from "../services/api";
import { getConflicts, createConflict, getConflict, updateResolutionStep, resolveConflict, reopenConflict } from "../services/conflictService";
// LoadingSpinner removido - usando SkeletonLoader
import { DashboardSkeleton } from "../components/SkeletonLoader";
import "./Dashboard.css";

// Lazy loading dos componentes pesados para melhor performance
const ConflictList = React.lazy(() => import("../components/ConflictList"));
const ConflictForm = React.lazy(() => import("../components/ConflictForm"));
const ConflictDetail = React.lazy(() => import("../components/ConflictDetail"));
const ConnectivityTest = React.lazy(() => import("../components/ConnectivityTest"));
const ChatButton = React.lazy(() => import("../components/chat/ChatButton"));

const Dashboard = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState([]);
  const [selectedConflictId, setSelectedConflictId] = useState(null);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [view, setView] = useState("list"); // "list", "form", "detail", "debug"
  const [showDebug, setShowDebug] = useState(false);

  // Hook para tratamento de erros
  const { error, handleError, clearError } = useErrorHandler();
  
  // Função auxiliar para limpar erro após delay
  const clearErrorAfterDelay = React.useCallback((message, delay = 5000) => {
    handleError(new Error(message), 'Dashboard');
  }, [handleError]);

  // Breadcrumbs baseados na view atual
  const getBreadcrumbs = () => {
    const baseBreadcrumbs = [
      { name: "Início", url: "/" },
      { name: "Dashboard", url: "/dashboard" }
    ];

    switch (view) {
      case "form":
        return [...baseBreadcrumbs, { name: "Novo Conflito", url: "/dashboard/novo" }];
      case "detail":
        return [...baseBreadcrumbs, { name: selectedConflict?.title || "Detalhes", url: `/dashboard/conflito/${selectedConflictId}` }];
      case "debug":
        return [...baseBreadcrumbs, { name: "Debug", url: "/dashboard/debug" }];
      default:
        return baseBreadcrumbs;
    }
  };

  // Dados estruturados para a página
  const getStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Dashboard - Casal Resolve",
    "description": "Gerencie seus conflitos de relacionamento, acompanhe o progresso e acesse ferramentas de resolução.",
    "url": "https://casal-resolve.vercel.app/dashboard",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Casal Resolve",
      "url": "https://casal-resolve.vercel.app"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": getBreadcrumbs().map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    }
  });

  useEffect(() => {
    // Verificar se o usuário está autenticado
    const token = localStorage.getItem("token");
    if (!token) {
      console.log("Token não encontrado, redirecionando para login");
      navigate("/login");
      return;
    } else {
      console.log("Token encontrado:", token.substring(0, 20) + "...");
      try {
        // Verificar se o token é válido decodificando-o
        const tokenData = JSON.parse(atob(token.split(".")[1]));
        console.log("Token decodificado:", tokenData);
        console.log("Token expira em:", new Date(tokenData.exp * 1000).toLocaleString());
      } catch (e) {
        console.error("Erro ao decodificar token:", e);
      }
    }

    // Testar conectividade com MongoDB
    const testMongoDB = async () => {
      try {
        console.log("Testando conectividade com MongoDB...");
        const result = await testMongoDBConnectivity();
        console.log("Resultado do teste de conectividade:", result);
      } catch (error) {
        console.error("Erro ao testar conectividade com MongoDB:", error);
      }
    };

    // Função para buscar dados do usuário e conflitos
    const fetchData = async () => {
      try {
        console.log("Iniciando busca de dados do usuário e conflitos");
        console.log("API baseURL:", api.defaults.baseURL);

        // Tentar buscar os dados do usuário da API
        try {
          console.log("Buscando perfil do usuário...");
          const response = await api.get("/user/profile");
          console.log("Perfil do usuário obtido:", response.data);
          setUserEmail(response.data.email);
          setIsAdmin(response.data.isAdmin || response.data.role === 'admin');
        } catch (apiError) {
          console.error("Erro ao buscar dados da API:", apiError);
          console.error("Detalhes do erro:", apiError.response ? apiError.response.data : "Sem resposta");

          // Fallback: extrair email do token se a API falhar
          try {
            const tokenData = JSON.parse(atob(token.split(".")[1]));
            console.log("Usando dados do token como fallback:", tokenData);
            setUserEmail(tokenData.email || "usuário@exemplo.com");
            setIsAdmin(tokenData.isAdmin || tokenData.role === 'admin');
          } catch (tokenError) {
            console.error("Erro ao decodificar token:", tokenError);
            setUserEmail("usuário@exemplo.com");
            setIsAdmin(false);
          }
        }

        // Buscar conflitos do usuário
        try {
          console.log("Buscando conflitos do usuário...");
          const conflictsData = await getConflicts();
          console.log("Conflitos obtidos:", conflictsData);
          setConflicts(conflictsData);
          // Limpar qualquer erro anterior se os conflitos forem carregados com sucesso
          clearError();
        } catch (conflictsError) {
          console.error("Erro ao buscar conflitos:", conflictsError);
          console.error("Detalhes do erro:", conflictsError.response ? conflictsError.response.data : "Sem resposta");
          clearErrorAfterDelay("Não foi possível carregar seus conflitos.");
        }
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
        clearErrorAfterDelay("Não foi possível carregar os dados.");
      } finally {
        // Sempre definir loading como false, independentemente do resultado
        setLoading(false);
      }
    };

    // Executar teste de MongoDB em paralelo (não bloquear o carregamento dos dados)
    testMongoDB().catch(error => {
      console.error("Erro no teste de conectividade (não crítico):", error);
    });

    // Executar busca de dados independentemente do teste de MongoDB
    fetchData();
  }, [navigate, clearErrorAfterDelay]);

  // Função para buscar um conflito específico
  const fetchConflictDetail = async (id) => {
    try {
      setLoading(true);
      const conflictData = await getConflict(id);
      setSelectedConflict(conflictData);
      setView("detail");
      setLoading(false);
    } catch (error) {
      console.error(`Erro ao buscar detalhes do conflito ${id}:`, error);
      clearErrorAfterDelay("Não foi possível carregar os detalhes do conflito.");
      setLoading(false);
    }
  };

  // Função para lidar com a seleção de um conflito
  const handleSelectConflict = (id) => {
    // Limpar qualquer mensagem de erro ao selecionar um conflito
    clearError();
    setSelectedConflictId(id);
    fetchConflictDetail(id);
  };

  // Função para lidar com a criação de um novo conflito
  const handleAddConflict = () => {
    // Limpar qualquer mensagem de erro ao adicionar um novo conflito
    clearError();
    setView("form");
  };

  // Função para lidar com o envio do formulário de conflito
  const handleSubmitConflict = async (conflictData) => {
    try {
      setLoading(true);
      const newConflict = await createConflict(conflictData);
      setConflicts([newConflict, ...conflicts]);
      setView("list");
      setLoading(false);
    } catch (error) {
      console.error("Erro ao criar conflito:", error);
      clearErrorAfterDelay("Não foi possível criar o conflito.");
      setLoading(false);
    }
  };

  // Função para atualizar uma etapa de resolução
  const handleUpdateStep = async (conflictId, stepNumber, userInput) => {
    try {
      setLoading(true);
      const updatedConflict = await updateResolutionStep(conflictId, stepNumber, userInput);
      setSelectedConflict(updatedConflict);

      // Atualizar a lista de conflitos
      setConflicts(conflicts.map(conflict =>
        conflict._id === conflictId ? updatedConflict : conflict
      ));

      setLoading(false);
    } catch (error) {
      console.error(`Erro ao atualizar etapa ${stepNumber}:`, error);
      clearErrorAfterDelay("Não foi possível atualizar a etapa de resolução.");
      setLoading(false);
    }
  };

  // Função para marcar um conflito como resolvido
  const handleResolveConflict = async (conflictId) => {
    try {
      setLoading(true);
      const resolvedConflict = await resolveConflict(conflictId);
      setSelectedConflict(resolvedConflict);

      // Atualizar a lista de conflitos
      setConflicts(conflicts.map(conflict =>
        conflict._id === conflictId ? resolvedConflict : conflict
      ));

      setLoading(false);
    } catch (error) {
      console.error(`Erro ao resolver conflito ${conflictId}:`, error);
      clearErrorAfterDelay("Não foi possível marcar o conflito como resolvido.");
      setLoading(false);
    }
  };

  // Função para reabrir um conflito resolvido
  const handleReopenConflict = async (conflictId) => {
    try {
      setLoading(true);

      // Buscar a lista atualizada de conflitos
      const conflictsData = await getConflicts();
      setConflicts(conflictsData);

      // Buscar o conflito atualizado
      const reopenedConflict = await getConflict(conflictId);
      setSelectedConflict(reopenedConflict);

      setLoading(false);
    } catch (error) {
      console.error(`Erro ao reabrir conflito ${conflictId}:`, error);
      clearErrorAfterDelay("Não foi possível reabrir o conflito.");
      setLoading(false);
    }
  };

  // Função para voltar à lista de conflitos
  const handleBackToList = () => {
    // Limpar qualquer mensagem de erro ao voltar para a lista
    clearError();
    setView("list");
    setSelectedConflictId(null);
    setSelectedConflict(null);
  };

  const handleLogout = () => {
    // Remover o token de autenticação
    localStorage.removeItem("token");
    console.log("Usuário deslogado");
    // Redirecionar para a página de login
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Dashboard - Gerencie seus Conflitos"
        description="Gerencie seus conflitos de relacionamento, acompanhe o progresso da resolução e acesse ferramentas de análise comportamental com IA."
        keywords="dashboard, conflitos, relacionamento, gestão, progresso, resolução, análise"
        url="https://casal-resolve.vercel.app/dashboard"
        type="website"
        structuredData={getStructuredData()}
      />
      
      <div className="dashboard-container">
        <header className="dashboard-header">
          <h1>Casal Resolve</h1>
          <div className="user-actions">
            <span className="user-email">{userEmail}</span>
            <button
              className="suggestions-button"
              onClick={() => navigate("/suggestions")}
              title="Minhas Sugestões"
              aria-label="Ir para página de sugestões"
            >
              💡
            </button>
            <button
              className="analytics-button"
              onClick={() => navigate("/analytics")}
              title="Analytics Avançado"
              aria-label="Ir para página de analytics"
            >
              📊
            </button>
            {isAdmin && (
              <>
                <button
                  className="debug-button"
                  onClick={() => setShowDebug(!showDebug)}
                  title="Ferramentas de diagnóstico"
                  aria-label="Alternar ferramentas de debug"
                >
                  🔧
                </button>
                <button
                  className="admin-button"
                  onClick={() => navigate("/admin")}
                  title="Painel de Administração"
                  aria-label="Ir para painel administrativo"
                >
                  👑
                </button>
              </>
            )}
            <Suspense fallback={<span>💬</span>}>
              <ChatButton className="header-chat-btn" />
            </Suspense>
            <button className="logout-button" onClick={handleLogout}>
              Sair
            </button>
          </div>
        </header>

        <Breadcrumb items={getBreadcrumbs()} />

        {error && (
          <div className="error-message" role="alert">
            <span>{error}</span>
            <button
              className="close-button"
              onClick={() => clearError()}
              aria-label="Fechar mensagem de erro"
            >
              ×
            </button>
          </div>
        )}

        <main className="dashboard-content">
          {showDebug && (
            <Suspense fallback={<DashboardSkeleton />}>
              <ConnectivityTest />
            </Suspense>
          )}

          {view === "list" && (
            <Suspense fallback={<DashboardSkeleton />}>
              <ConflictList
                conflicts={conflicts}
                onSelectConflict={handleSelectConflict}
                onAddConflict={handleAddConflict}
              />
            </Suspense>
          )}

          {view === "form" && (
            <Suspense fallback={<DashboardSkeleton />}>
              <ConflictForm
                onSubmit={handleSubmitConflict}
                onCancel={handleBackToList}
              />
            </Suspense>
          )}

          {view === "detail" && selectedConflict && (
            <Suspense fallback={<DashboardSkeleton />}>
              <ConflictDetail
                conflict={selectedConflict}
                onUpdateStep={handleUpdateStep}
                onResolve={handleResolveConflict}
                onReopen={handleReopenConflict}
                onBack={handleBackToList}
              />
            </Suspense>
          )}
        </main>

        <footer className="dashboard-footer">
          <p>&copy; 2025 Casal Resolve - Plataforma para resolução de conflitos</p>
        </footer>

        {/* Chat flutuante para acesso rápido */}
        <Suspense fallback={null}>
          <ChatButton 
            className="floating" 
            conflictContext={selectedConflict ? {
              conflictId: selectedConflict._id,
              action: 'shared',
              details: selectedConflict.title
            } : null}
          />
        </Suspense>
      </div>
    </>
  );
};

export default Dashboard;
